import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config/api';

export const DynamicForm = ({ formSlug, isPopup = false, onSuccess }) => {
  const [formConfig, setFormConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [fileData, setFileData] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchFormConfig();
  }, [formSlug]);

  const fetchFormConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/public/forms/${formSlug}`);
      
      if (!response.ok) {
        throw new Error('Form not found');
      }
      
      const data = await response.json();
      setFormConfig(data.form);
      
      // Initialize form data with empty values
      const initialData = {};
      data.form.fields.forEach(field => {
        if (field.field_type === 'checkbox') {
          initialData[field.key] = [];
        } else {
          initialData[field.key] = '';
        }
      });
      setFormData(initialData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldKey, value) => {
    setFormData(prev => ({ ...prev, [fieldKey]: value }));
    // Clear validation error for this field
    if (validationErrors[fieldKey]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  const handleFileChange = (fieldKey, files, field) => {
    const constraints = field.upload_constraints || {};
    const allowedTypes = constraints.allowed_types || ['pdf', 'jpg', 'jpeg', 'png'];
    const maxSizeMB = constraints.max_size_mb || 5;
    const maxCount = constraints.max_count || 1;

    // Validate file count
    if (files.length > maxCount) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldKey]: `Maximum ${maxCount} file(s) allowed`
      }));
      return;
    }

    // Validate each file
    const errors = [];
    for (let file of files) {
      const ext = file.name.split('.').pop().toLowerCase();
      const sizeMB = file.size / (1024 * 1024);

      if (!allowedTypes.includes(ext)) {
        errors.push(`${file.name}: Invalid type. Allowed: ${allowedTypes.join(', ')}`);
      }
      if (sizeMB > maxSizeMB) {
        errors.push(`${file.name}: Size (${sizeMB.toFixed(2)}MB) exceeds ${maxSizeMB}MB`);
      }
    }

    if (errors.length > 0) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldKey]: errors.join('; ')
      }));
      return;
    }

    // Store files
    setFileData(prev => ({ ...prev, [fieldKey]: Array.from(files) }));
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldKey];
      return newErrors;
    });
  };

  const handleCheckboxChange = (fieldKey, optionValue, checked) => {
    setFormData(prev => {
      const currentValues = prev[fieldKey] || [];
      if (checked) {
        return { ...prev, [fieldKey]: [...currentValues, optionValue] };
      } else {
        return { ...prev, [fieldKey]: currentValues.filter(v => v !== optionValue) };
      }
    });
  };

  const validateStep = (stepFields) => {
    const errors = {};
    stepFields.forEach(field => {
      if (field.required) {
        const value = formData[field.key];
        if (!value || (Array.isArray(value) && value.length === 0)) {
          errors[field.key] = 'This field is required';
        }
      }
      
      // Email validation
      if (field.field_type === 'email' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors[field.key] = 'Please enter a valid email address';
        }
      }

      // File validation
      if (field.field_type === 'file' && field.required) {
        if (!fileData[field.key] || fileData[field.key].length === 0) {
          errors[field.key] = 'Please upload a file';
        }
      }
    });
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all required fields
    const allErrors = validateStep(formConfig.fields);
    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      // Scroll to first error
      const firstErrorField = Object.keys(allErrors)[0];
      const errorElement = document.getElementById(`field-${firstErrorField}`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('form_slug', formSlug);
      formDataToSend.append('data', JSON.stringify(formData));
      formDataToSend.append('source_page', window.location.href);

      // Append files with field key in filename
      Object.entries(fileData).forEach(([fieldKey, files]) => {
        files.forEach((file, index) => {
          // Rename file to include field_key for backend processing
          const newFile = new File([file], `${fieldKey}_${index}`, { type: file.type });
          formDataToSend.append('files', newFile);
        });
      });

      const response = await fetch(`${backendUrl}/api/forms/submit-dynamic`, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Submission failed');
      }

      setSubmitted(true);

      // Handle success behavior (skip redirect if in popup)
      if (!isPopup && formConfig.success_mode === 'redirect' && result.redirect_url) {
        setTimeout(() => {
          window.location.href = result.redirect_url;
        }, 1500);
      }

      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNextStep = () => {
    // Get step keys and current step key
    const steps = {};
    formConfig.fields.forEach(field => {
      const step = field.step_group_id || 'step-1';
      if (!steps[step]) steps[step] = [];
      steps[step].push(field);
    });
    
    const stepKeys = Object.keys(steps).sort();
    const currentStepKey = stepKeys[currentStep - 1];
    const currentStepFields = steps[currentStepKey] || [];
    
    const errors = validateStep(currentStepFields);
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setCurrentStep(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderField = (field) => {
    const value = formData[field.key] || '';
    const error = validationErrors[field.key];
    const fieldId = `field-${field.key}`;

    const commonClasses = `w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
      error 
        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
        : 'border-[#E5E7EB] focus:border-[#1E40AF] focus:ring-2 focus:ring-[#DBEAFE]'
    }`;

    return (
      <div key={field.key} id={fieldId} className="mb-6">
        <label className="block text-sm font-semibold text-[#111827] mb-2">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        {field.description && (
          <p className="text-sm text-[#6B7280] mb-2">{field.description}</p>
        )}

        {/* Text Input */}
        {['text', 'email', 'phone', 'number'].includes(field.field_type) && (
          <input
            type={field.field_type === 'phone' ? 'tel' : field.field_type}
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.placeholder || ''}
            required={field.required}
            className={commonClasses}
          />
        )}

        {/* Textarea */}
        {field.field_type === 'textarea' && (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.placeholder || ''}
            required={field.required}
            rows={4}
            className={commonClasses}
          />
        )}

        {/* Date */}
        {field.field_type === 'date' && (
          <input
            type="date"
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            required={field.required}
            className={commonClasses}
          />
        )}

        {/* Select Dropdown */}
        {field.field_type === 'select' && (
          <select
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            required={field.required}
            className={commonClasses}
          >
            <option value="">-- Select --</option>
            {(field.options || []).map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}

        {/* Radio Buttons */}
        {field.field_type === 'radio' && (
          <div className="space-y-2">
            {(field.options || []).map(opt => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={field.key}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  required={field.required}
                  className="w-4 h-4 text-[#1E40AF]"
                />
                <span className="text-sm text-[#111827]">{opt.label}</span>
              </label>
            ))}
          </div>
        )}

        {/* Checkboxes */}
        {field.field_type === 'checkbox' && (
          <div className="space-y-2">
            {(field.options || []).map(opt => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  value={opt.value}
                  checked={(value || []).includes(opt.value)}
                  onChange={(e) => handleCheckboxChange(field.key, opt.value, e.target.checked)}
                  className="w-4 h-4 text-[#1E40AF] rounded"
                />
                <span className="text-sm text-[#111827]">{opt.label}</span>
              </label>
            ))}
          </div>
        )}

        {/* File Upload */}
        {field.field_type === 'file' && (
          <div>
            <input
              type="file"
              multiple={field.upload_constraints?.max_count > 1}
              accept={field.upload_constraints?.allowed_types?.map(t => `.${t}`).join(',')}
              onChange={(e) => handleFileChange(field.key, e.target.files, field)}
              required={field.required}
              className="w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#1E40AF] file:text-white hover:file:bg-[#1e3a8a]"
            />
            {field.upload_constraints && (
              <p className="text-xs text-[#6B7280] mt-1">
                Allowed: {field.upload_constraints.allowed_types?.join(', ').toUpperCase()} | 
                Max size: {field.upload_constraints.max_size_mb}MB | 
                Max files: {field.upload_constraints.max_count}
              </p>
            )}
            {fileData[field.key] && fileData[field.key].length > 0 && (
              <div className="mt-2 space-y-1">
                {fileData[field.key].map((file, idx) => (
                  <div key={idx} className="text-xs text-[#10B981] flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-3 border-[#1E40AF] border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-2 text-sm text-[#6B7280]">Loading form...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 bg-red-50 border-2 border-red-200 rounded-lg">
        <svg className="w-12 h-12 mx-auto text-red-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="text-lg font-bold text-red-900 mb-2">Form Not Found</h3>
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="text-center py-12 bg-green-50 border-2 border-green-200 rounded-lg">
        <svg className="w-16 h-16 mx-auto text-green-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="text-xl font-bold text-green-900 mb-3">
          {formConfig.success_message || 'Thank you! Your submission has been received.'}
        </h3>
        {!isPopup && (formConfig.success_mode === 'both' || formConfig.success_mode === 'redirect') && formConfig.success_redirect_url && (
          <a
            href={formConfig.success_redirect_url}
            className="inline-block px-6 py-3 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors"
          >
            Continue
          </a>
        )}
      </div>
    );
  }

  // Guided Mode
  if (formConfig.ux_mode === 'guided') {
    const steps = {};
    formConfig.fields.forEach(field => {
      const step = field.step_group_id || 'step-1';
      if (!steps[step]) steps[step] = [];
      steps[step].push(field);
    });

    const stepKeys = Object.keys(steps).sort();
    const totalSteps = stepKeys.length;
    const currentStepIndex = currentStep - 1;
    const currentStepKey = stepKeys[currentStepIndex];
    const currentStepFields = steps[currentStepKey] || [];

    return (
      <div className="max-w-2xl mx-auto">
        {/* Progress Bar */}
        {formConfig.show_progress && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#111827]">
                Step {currentStep} of {totalSteps}
              </span>
              <span className="text-sm text-[#6B7280]">
                {Math.round((currentStep / totalSteps) * 100)}%
              </span>
            </div>
            <div className="w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#1E40AF] transition-all duration-300 rounded-full"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border-2 border-[#E5E7EB] p-6 md:p-8">
          {currentStepFields.map(field => renderField(field))}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-4 mt-8">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={handlePrevStep}
                className="px-6 py-3 bg-white border-2 border-[#E5E7EB] text-[#6B7280] font-semibold rounded-lg hover:bg-[#F3F4F6] transition-colors"
              >
                ← Previous
              </button>
            ) : (
              <div />
            )}

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={handleNextStep}
                className="px-6 py-3 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors ml-auto"
              >
                Next →
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
              >
                {submitting ? 'Submitting...' : formConfig.submit_cta_text}
              </button>
            )}
          </div>
        </form>
      </div>
    );
  }

  // Normal Scroll Mode
  return (
    <div className="max-w-3xl mx-auto">
      <form onSubmit={handleSubmit} className="bg-white rounded-xl border-2 border-[#E5E7EB] p-6 md:p-8">
        {formConfig.fields.map(field => renderField(field))}

        <div className="mt-8">
          <button
            type="submit"
            disabled={submitting}
            className="w-full px-6 py-3 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting...' : formConfig.submit_cta_text}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DynamicForm;
